import * as React from 'react';
import classNames from 'classnames';
import {
  ElementOf,
  IActionProps,
  Mode,
  PopupIntentStyle,
  tuple,
} from '../common/props';
import * as Classes from '../common/classes';
import { Icon } from '../icon/Icon';
import { Popup, PopupPosition } from '../popover/Popup';
import { ForwardedRef, ReactNode, useState } from 'react';
import { Tooltip } from '../tooltip/Tooltip';
import { mcDropDown, mcMenuKebab } from '@mc/react-ui-icons';
import { useInverseBg } from '../theme/useInverseBg';
import { InverseBgProvider } from '../theme/InverseBg';
import { LinkProps, Link } from '../link/Link';

export interface IButtonProps extends Omit<IActionProps, 'style'> {
  /**
   * Má se komponenta roztáhnout na plnou šířku nadřazeného elementu? Výchozí hodnota je vypnuto.
   */
  fill?: boolean;

  /**
   * Pokud je true, tak prvek je "visibility hidden" a předpokládá se, že je v něčem, co má styl HOVER_CONTAINER.
   */
  showOnHover?: boolean;
  /**
   * Pokud je uvedeno, bude tlačítko jako dropdown - pro textové tlačítko se k text přidá šipka dolu, pro tlačítko jen s ikonou bude menu zobrazeno po kliknutí na ikonu, pokud nebude uveden ani text ani ikona, zvolí se ikona na tři tečky.
   */
  menu?: JSX.Element | ((closePopup: () => void) => JSX.Element);
  /**
   * Html properta typu tlačítka.
   */
  type?: 'submit' | 'reset' | 'button';
  /**
   * Styl tlačítka, výchozí hodnota je DEFAULT.
   */
  style?: ButtonStyle;
  cssStyle?: React.CSSProperties;
  textCenter?: boolean;
  /**
   * Vynucení dropdown ikony (např. místo trojtečky).
   */
  dropdown?: boolean;
  /**
   * Inverzní verze tlačítka - typicky na tmavším (jinými slovy jiném něž obvykle, např. danger apod.) pozadí.
   */
  inverse?: boolean;
  /**
   * Výstražná varianta tlačítka (např. primární danger apod.). Typicky se používá pro akce, které nelze vrátit (např. mazání apod.).
   */
  danger?: boolean;
  inheritColor?: boolean;
  /**
   * Pokud je true, tak tlačítko nemá horzizontální padding.
   */
  noPadding?: boolean;
  /**
   * Pokud je true, tak tlačítko nemá minimální šířku nebo doporučenou šířku, je velké jako jeho obsah.
   */
  noDefaultWidth?: boolean;
  /**
   * Pokud se má tlačítko vyrenderovat jako link (typicky LINK tlačíkot), předá se tato properta a button je pak obalený a href.
   */
  link?: LinkProps;
  /**
   * Standardně tato komponenta nastavuje u tlačítka stop propagation u click eventu - je to 90% použití, pokud by to někdo nechtěl, může to tímto příznakem vypnout.
   */
  disableStopPropagation?: boolean;
  title?: string;
  /**
   * Pokud je true, nemá při focus žádné zvýraznění.
   */
  noFocus?: boolean;
  /**
   * Nutno zapnout pokud je tlačítko s OnClick na řádku tabulky/listu s linkem, jinak se OnClick spustí i link.
   *
   */
  preventDefault?: boolean;
  /**
   * Tlačítko implicitně nastavuje closeOnInnerClick na true, protože typicky je tam menu a při kliknutí na položku se má dropdown zavřít,
   * pokud se má při dropdown zobrazit něco jiného bez této funkce,
   * je třeba to předat přes popupProps (closeOnInnerClick: false).
   */
  popupProps?: {
    closeOnInnerClick?: boolean;
    arrow?: boolean;
    className?: string;
    position?: PopupPosition | PopupPosition[];
    mode?: Mode;
    focusContentOnOpen?: boolean;
    nested?: boolean;
  };
  tabIndex?: number | undefined;
  /**
   * Pro tooltip na tlačítku je preferována tato varianta, protože pokud se jedná o dropdown tlačítko, tak jen použití tlačítka v tooltip komponentě nechodí - je obalen dvakrát, tento vstup to ale řeší.
   */
  tooltip?: ReactNode;
  tooltipProps?: {
    intent?: PopupIntentStyle;
    mode?: Mode;
  };

  /**
   * schová u tlačítka ikonu, která je defaultně zobrazena u tlačítka s menu.
   */
  hideMenuDropdownIcon?: boolean;
}

/**
 * Základní typy stylu tlačítka.
 * Typ 'none' slouží pouze pro tvorbu dalších talčítek v ui-kitu
 */
export const ButtonStyles = tuple('default', 'primary', 'link', 'none');
export type ButtonStyle = ElementOf<typeof ButtonStyles>;

export type ButtonProps = IButtonProps;

const DEFAULT_POSITIONS: PopupPosition[] = [
  'bottom left',
  'bottom right',
  'top left',
  'top right',
];

export const ButtonIn: React.FC<
  ButtonProps & { innerRef?: ForwardedRef<HTMLButtonElement> }
> = ({
  tooltip,
  tooltipProps,
  innerRef,
  noFocus,
  tabIndex,
  title,
  inverse,
  disableStopPropagation,
  onClick,
  dropdown,
  showOnHover,
  menu,
  className,
  noPadding,
  noDefaultWidth,
  children,
  fill,
  inheritColor,
  icon,
  iconRight,
  popupProps,
  danger,
  cssStyle,
  style = 'default',
  link,
  disabled,
  preventDefault,
  dataTest,
  hideMenuDropdownIcon,
  textCenter,
  ...other
}) => {
  const parentInverse = useInverseBg(inverse);

  let useIcon;
  if (menu && !children && !icon) {
    useIcon = dropdown ? (
      <Icon fixedWidth icon={mcDropDown} />
    ) : (
      <Icon fixedWidth icon={mcMenuKebab} />
    );
  } else {
    useIcon = icon;
  }

  const showAsDropdown = menu && !!children && !hideMenuDropdownIcon;
  const showRightIcon = iconRight && !menu && children && iconRight;

  let elementOnClick = undefined;
  if (disableStopPropagation) {
    elementOnClick = onClick;
  } else {
    elementOnClick = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (preventDefault) {
        e.preventDefault();
      }
      onClick?.(e);
    };
  }
  const calculatedNoPadding = noPadding || (style === 'link' && link);
  const button = (
    <button
      data-test={dataTest ? `button-${dataTest}` : undefined}
      ref={innerRef}
      style={cssStyle}
      type={'button'}
      title={title}
      tabIndex={tabIndex}
      onClick={menu || link ? undefined : elementOnClick}
      className={classNames(className, Classes.BUTTON, {
        [Classes.BUTTON_NO_FOCUS]: noFocus,
        [Classes.BUTTON_FILL]: fill,
        [Classes.BUTTON_DEFAULT_WIDTH]: noDefaultWidth,
        [Classes.BUTTON_DROPDOWN]: menu,
        [Classes.SHOW_ON_HOVER]: showOnHover,
        [Classes.BUTTON_INVERSE]: parentInverse,
        [Classes.BUTTON_NO_PADDING]: calculatedNoPadding,
        // [Classes.BUTTON_NO_PADDING]: style === 'link',
        [Classes.BUTTON_INHERIT_COLOR]: inheritColor,
        [Classes.BUTTON_DEFAULT]: style === 'default',
        [Classes.BUTTON_PRIMARY]: style === 'primary',
        [Classes.BUTTON_LINK]:
          style === 'link' && !!children && !calculatedNoPadding,
        [Classes.BUTTON_LINK_NO_PADDING]:
          style === 'link' && !!children && calculatedNoPadding,
        [Classes.BUTTON_ICON_LINK]: style === 'link' && !children,
        [Classes.BUTTON_DEFAULT_DANGER]: style === 'default' && danger,
        [Classes.BUTTON_PRIMARY_DANGER]: style === 'primary' && danger,
        [Classes.BUTTON_LINK_DANGER]: style === 'link' && danger,
        [Classes.BUTTON_ICON_ONLY]: !children,
        [Classes.BUTTON_TEXT_CENTER]: textCenter,
      })}
      disabled={disabled}
      {...other}
    >
      {useIcon && <div className={Classes.BUTTON_ICON_LEFT}>{useIcon}</div>}
      <div className={Classes.BUTTON_TEXT}>{children}</div>
      {showAsDropdown && (
        <div className={Classes.BUTTON_ICON_RIGHT}>
          <Icon icon={mcDropDown} />
        </div>
      )}
      {showRightIcon && (
        <div className={Classes.BUTTON_ICON_RIGHT}>{iconRight}</div>
      )}
    </button>
  );

  const { closeOnInnerClick, position, mode, ...restPopupProps } =
    popupProps || {};

  const calculatedPosition = React.useMemo(() => {
    return position
      ? Array.isArray(position)
        ? [...position, ...DEFAULT_POSITIONS]
        : [position, ...DEFAULT_POSITIONS]
      : DEFAULT_POSITIONS;
  }, [position]);

  const [open, setOpen] = useState(false);
  let content;
  if (menu) {
    content = (
      <Popup
        position={calculatedPosition}
        {...restPopupProps}
        mode={mode !== undefined ? mode : 'view'}
        disabled={disabled}
        trigger={(open) => {
          setOpen(open);
          return button;
        }}
      >
        {(closePopup: () => void) => {
          return (
            <div
              onClick={
                closeOnInnerClick === undefined || closeOnInnerClick
                  ? closePopup
                  : undefined
              }
            >
              {typeof menu === 'function' ? menu(closePopup) : menu}
            </div>
          );
        }}
      </Popup>
    );
  } else {
    content = button;
  }

  content = <InverseBgProvider inverse={false}>{content}</InverseBgProvider>;

  if (tooltip) {
    if (menu) {
      content = (
        <Tooltip {...tooltipProps} trigger={<div>{content}</div>}>
          {open ? undefined : tooltip}
        </Tooltip>
      );
    } else {
      content = (
        <Tooltip {...tooltipProps} trigger={<div>{content}</div>}>
          {tooltip}
        </Tooltip>
      );
    }
  }

  if (link) {
    if (disableStopPropagation) {
      return <Link {...link}>{content}</Link>;
    } else {
      return (
        <Link
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            e.stopPropagation();
          }}
          {...link}
        >
          {content}
        </Link>
      );
    }
  } else {
    return content;
  }
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    return <ButtonIn innerRef={ref} {...props} />;
  }
);
